import { call, put, takeEvery, takeLatest, select, fork, delay } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { 
  workOrderApi, 
  SearchWorkOrdersRequest, 
  SearchWorkOrdersResponse,
  CreateWorkOrderRequest, 
  OperationLogRequest,
  WorkOrder,
  WorkOrderSerial,
  WorkOrderOperationLog,
  WorkOrderStatus,
  RouteCard
} from '../../services/workOrderApi';
import { purchaseOrderApi } from '../../services/purchaseOrderApi';
import {
  fetchWorkOrdersStart,
  fetchWorkOrdersSuccess,
  fetchWorkOrdersFailure,
  fetchWorkOrderByIdStart,
  fetchWorkOrderByIdSuccess,
  fetchWorkOrderByIdFailure,
  createWorkOrderStart,
  createWorkOrderSuccess,
  createWorkOrderFailure,
  updateWorkOrderStart,
  updateWorkOrderSuccess,
  updateWorkOrderFailure,
  deleteWorkOrderStart,
  deleteWorkOrderSuccess,
  deleteWorkOrderFailure,
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,
  fetchOperationLogsStart,
  fetchOperationLogsSuccess,
  fetchOperationLogsFailure,
  createOperationLogStart,
  createOperationLogSuccess,
  createOperationLogFailure,
  updateOperationLogStart,
  updateOperationLogSuccess,
  updateOperationLogFailure,
  fetchSerialsStart,
  fetchSerialsSuccess,
  fetchSerialsFailure,
  approveSerialStart,
  approveSerialSuccess,
  rejectSerialStart,
  rejectSerialSuccess,
  updateWorkOrderRealtime,
  addOperationLogRealtime
} from '../reducers/workOrderSlice';
import { RootState } from '../index';

// Type for the actual HTTP response we're receiving
interface ApiHttpResponse {
  success: boolean;
  statusCode: number;
  headers: any;
  body: string;
}

// Fetch Work Orders
function* fetchWorkOrdersApi(action: PayloadAction<SearchWorkOrdersRequest | undefined>) {
  console.log('WorkOrder Saga: fetchWorkOrdersApi triggered!');
  console.log('WorkOrder Saga: Action type:', action.type);
  console.log('WorkOrder Saga: Action payload:', action.payload);
  
  try {
    console.log('WorkOrder Saga: Starting fetchWorkOrdersApi');
    
    const state: RootState = yield select();
    const { currentPage, pageSize, searchTerm, statusFilter, isReworkFilter } = state.workOrder;
    
    const params: SearchWorkOrdersRequest = {
      pageNum: currentPage,
      pageSize: pageSize,
      sort: 'CreatedDate desc',
      filter: {
        ...(action.payload?.filter || {}),
        ...(isReworkFilter !== null && { IsRework: isReworkFilter }),
        ...(statusFilter && { WOStatusId: statusFilter }),
        ...(searchTerm && { WONum: searchTerm }), // Assuming search by work order number
      },
      ...action.payload,
    };

    console.log('WorkOrder Saga: Params being sent to API:', params);
    console.log('WorkOrder Saga: Calling workOrderApi.getAll with params:', params);
    
    const response: ApiHttpResponse = yield call(workOrderApi.getAll, params);
    
    console.log('WorkOrder Saga: API response received:', response);
    console.log('WorkOrder Saga: Response type:', typeof response);
    console.log('WorkOrder Saga: Response keys:', response ? Object.keys(response) : 'null/undefined');
    console.log('WorkOrder Saga: Response.body:', response?.body);
    console.log('WorkOrder Saga: Response.body type:', typeof response?.body);
    console.log('WorkOrder Saga: Response.body length:', response?.body?.length);
    
    // The API is returning the full HTTP response, so we need to parse the body
    let parsedResponse;
    if (response && response.body && typeof response.body === 'string') {
      try {
        parsedResponse = JSON.parse(response.body);
        console.log('WorkOrder Saga: Parsed response body:', parsedResponse);
      } catch (error) {
        console.error('WorkOrder Saga: Failed to parse response body:', error);
        throw new Error('Invalid JSON response from API');
      }
    } else {
      console.error('WorkOrder Saga: Unexpected response structure:', response);
      throw new Error('Unexpected response structure from API');
    }
    
    console.log('WorkOrder Saga: Final parsed response:', parsedResponse);
    console.log('WorkOrder Saga: Dispatching fetchWorkOrdersSuccess with:', parsedResponse);
    
    yield put(fetchWorkOrdersSuccess(parsedResponse));
    
    console.log('WorkOrder Saga: fetchWorkOrdersSuccess dispatched successfully');
    
  } catch (error: any) {
    console.error('WorkOrder Saga: Error occurred:', error);
    console.error('WorkOrder Saga: Error message:', error.message);
    console.error('WorkOrder Saga: Error stack:', error.stack);
    yield put(fetchWorkOrdersFailure(error.message || 'Failed to fetch work orders'));
  }
}

// Fetch Work Order by ID
function* fetchWorkOrderByIdApi(action: PayloadAction<string>) {
  try {
    const workOrder: WorkOrder = yield call(workOrderApi.getById, action.payload);
    yield put(fetchWorkOrderByIdSuccess(workOrder));
  } catch (error: any) {
    yield put(fetchWorkOrderByIdFailure(error.message || 'Failed to fetch work order'));
  }
}

// Create Work Order
function* createWorkOrderApi(action: PayloadAction<CreateWorkOrderRequest>) {
  try {
    const newWorkOrder: WorkOrder = yield call(workOrderApi.create, action.payload);
    yield put(createWorkOrderSuccess(newWorkOrder));
    
    // Refresh the work orders list to show the new item
    yield put(fetchWorkOrdersStart());
  } catch (error: any) {
    yield put(createWorkOrderFailure(error.message || 'Failed to create work order'));
  }
}

// Update Work Order
function* updateWorkOrderApi(action: PayloadAction<Partial<WorkOrder>>) {
  try {
    const updatedWorkOrder: WorkOrder = yield call(workOrderApi.update, action.payload);
    yield put(updateWorkOrderSuccess(updatedWorkOrder));
  } catch (error: any) {
    yield put(updateWorkOrderFailure(error.message || 'Failed to update work order'));
  }
}

// Delete Work Order
function* deleteWorkOrderApi(action: PayloadAction<string>) {
  try {
    yield call(workOrderApi.delete, action.payload);
    yield put(deleteWorkOrderSuccess(action.payload));
  } catch (error: any) {
    yield put(deleteWorkOrderFailure(error.message || 'Failed to delete work order'));
  }
}

// Fetch Dropdown Data
function* fetchDropdownsApi() {
  try {
    // Fetch data in parallel
    const [workOrderStatusesResponse, routeCardsResponse]: [WorkOrderStatus[], RouteCard[]] = yield Promise.all([
      call(workOrderApi.getWorkOrderStatuses),
      call(workOrderApi.getRouteCards)
    ]);

    yield put(fetchDropdownsSuccess({
      workOrderStatuses: workOrderStatusesResponse,
      routeCards: routeCardsResponse
    }));
  } catch (error: any) {
    yield put(fetchDropdownsFailure(error.message || 'Failed to fetch dropdown data'));
  }
}

// Fetch Operation Logs
function* fetchOperationLogsApi(action: PayloadAction<string>) {
  try {
    const operationLogs: WorkOrderOperationLog[] = yield call(
      workOrderApi.getOperationLogs, 
      action.payload
    );
    yield put(fetchOperationLogsSuccess(operationLogs));
  } catch (error: any) {
    yield put(fetchOperationLogsFailure(error.message || 'Failed to fetch operation logs'));
  }
}

// Create Operation Log
function* createOperationLogApi(action: PayloadAction<OperationLogRequest>) {
  try {
    const newLog: WorkOrderOperationLog = yield call(workOrderApi.createOperationLog, action.payload);
    yield put(createOperationLogSuccess(newLog));
  } catch (error: any) {
    yield put(createOperationLogFailure(error.message || 'Failed to create operation log'));
  }
}

// Update Operation Log
function* updateOperationLogApi(action: PayloadAction<Partial<WorkOrderOperationLog>>) {
  try {
    const updatedLog: WorkOrderOperationLog = yield call(workOrderApi.updateOperationLog, action.payload);
    yield put(updateOperationLogSuccess(updatedLog));
  } catch (error: any) {
    yield put(updateOperationLogFailure(error.message || 'Failed to update operation log'));
  }
}

// Fetch Serials
function* fetchSerialsApi(action: PayloadAction<string>) {
  try {
    const serials: WorkOrderSerial[] = yield call(workOrderApi.getSerials, action.payload);
    yield put(fetchSerialsSuccess(serials));
  } catch (error: any) {
    yield put(fetchSerialsFailure(error.message || 'Failed to fetch serials'));
  }
}

// Approve Serial
function* approveSerialApi(action: PayloadAction<{ workOrderId: string; serialNum: string }>) {
  try {
    yield call(workOrderApi.approveSerial, action.payload.workOrderId, action.payload.serialNum);
    yield put(approveSerialSuccess(action.payload.serialNum));
    
    // Create operation log for approval
    const state: RootState = yield select();
    const currentUser = state.auth.user; // Assuming we have auth state
    
    if (currentUser && state.workOrder.selectedWorkOrder) {
      const logData: OperationLogRequest = {
        WOKey: action.payload.workOrderId,
        OperatorKey: currentUser.id,
        RCTKey: state.workOrder.selectedWorkOrder.route_card?.id || '',
        OperationKey: '', // Will need to be determined from context
        SerialNumber: action.payload.serialNum,
        Status: 'PASSED'
      };
      
      yield put(createOperationLogStart(logData));
    }
  } catch (error: any) {
    console.error('Failed to approve serial:', error);
    // Don't show error to user for approve/reject failures as they might be temporary
  }
}

// Reject Serial
function* rejectSerialApi(action: PayloadAction<{ workOrderId: string; serialNum: string; reason?: string }>) {
  try {
    yield call(workOrderApi.rejectSerial, action.payload.workOrderId, action.payload.serialNum, action.payload.reason);
    yield put(rejectSerialSuccess(action.payload.serialNum));
    
    // Create operation log for rejection
    const state: RootState = yield select();
    const currentUser = state.auth.user;
    
    if (currentUser && state.workOrder.selectedWorkOrder) {
      const logData: OperationLogRequest = {
        WOKey: action.payload.workOrderId,
        OperatorKey: currentUser.id,
        RCTKey: state.workOrder.selectedWorkOrder.route_card?.id || '',
        OperationKey: '',
        SerialNumber: action.payload.serialNum,
        Status: 'FAILED'
      };
      
      yield put(createOperationLogStart(logData));
    }
  } catch (error: any) {
    console.error('Failed to reject serial:', error);
  }
}

// Auto-refresh Work Orders (for real-time updates when WebSocket is not available)
function* autoRefreshWorkOrders() {
  while (true) {
    try {
      const state: RootState = yield select();
      
      // Only auto-refresh if live updates are enabled and we're not currently loading
      if (state.workOrder.liveUpdates && !state.workOrder.loading) {
        yield put(fetchWorkOrdersStart());
      }
      
      // Wait 2 minutes before next refresh (reduced frequency)
      yield delay(120000);
    } catch (error) {
      console.error('Auto refresh error:', error);
      yield delay(180000); // Wait 3 minutes on error
    }
  }
}

// Handle real-time updates from WebSocket
function* handleRealtimeUpdate(action: PayloadAction<any>) {
  try {
    // This would typically be called by WebSocket listeners
    const { type, data } = action.payload;
    
    switch (type) {
      case 'WORK_ORDER_UPDATED':
        yield put(updateWorkOrderRealtime(data));
        break;
      case 'OPERATION_LOG_ADDED':
        yield put(addOperationLogRealtime(data));
        break;
      default:
        console.log('Unknown real-time update type:', type);
    }
  } catch (error: any) {
    console.error('Real-time update error:', error);
  }
}

// Watch for search term changes and debounce the search
function* watchSearchTerm() {
  yield takeLatest('workOrder/setSearchTerm', function* () {
    yield delay(500); // Debounce for 500ms
    yield put(fetchWorkOrdersStart());
  });
}

// Watch for filter changes
function* watchFilters() {
  yield takeLatest(['workOrder/setStatusFilter', 'workOrder/setIsReworkFilter'], function* () {
    yield put(fetchWorkOrdersStart());
  });
}

// Watch for pagination changes
function* watchPagination() {
  yield takeLatest(['workOrder/setPage', 'workOrder/setPageSize'], function* () {
    yield put(fetchWorkOrdersStart());
  });
}

// Root Work Order Saga
export function* workOrderSaga() {
  console.log('WorkOrder Saga: Saga registered and listening for actions');
  
  // Main API calls
  yield takeEvery(fetchWorkOrdersStart.type, fetchWorkOrdersApi);
  console.log('WorkOrder Saga: Listening for fetchWorkOrdersStart.type:', fetchWorkOrdersStart.type);
  
  yield takeEvery(fetchWorkOrderByIdStart.type, fetchWorkOrderByIdApi);
  yield takeEvery(createWorkOrderStart.type, createWorkOrderApi);
  yield takeEvery(updateWorkOrderStart.type, updateWorkOrderApi);
  yield takeEvery(deleteWorkOrderStart.type, deleteWorkOrderApi);
  
  // Dropdown data
  yield takeEvery(fetchDropdownsStart.type, fetchDropdownsApi);
  
  // Operation logs
  yield takeEvery(fetchOperationLogsStart.type, fetchOperationLogsApi);
  yield takeEvery(createOperationLogStart.type, createOperationLogApi);
  yield takeEvery(updateOperationLogStart.type, updateOperationLogApi);
  
  // Serials
  yield takeEvery(fetchSerialsStart.type, fetchSerialsApi);
  yield takeEvery(approveSerialStart.type, approveSerialApi);
  yield takeEvery(rejectSerialStart.type, rejectSerialApi);
  
  // Real-time updates
  yield takeEvery('REALTIME_UPDATE', handleRealtimeUpdate);
  
  // Background tasks
  yield fork(autoRefreshWorkOrders);
  yield fork(watchSearchTerm);
  yield fork(watchFilters);
  yield fork(watchPagination);
  
  console.log('WorkOrder Saga: All listeners registered successfully');
} 